import {ComponentType, useEffect} from 'react';
import {datadogRum} from '@datadog/browser-rum';

const applicationId = process.env.REACT_APP_DD_APPLICATION_ID;
const clientToken = process.env.REACT_APP_DD_CLIENT_TOKEN;

/**
 * Initializes Datadog RUM (Real User Monitoring).
 *
 * Replaces the previous Elastic APM RUM setup. Credentials are read from
 * environment variables so nothing sensitive lives in the source tree:
 *  - REACT_APP_DD_APPLICATION_ID (required)
 *  - REACT_APP_DD_CLIENT_TOKEN   (required)
 *  - REACT_APP_DD_SITE           (optional, defaults to datadoghq.com)
 *  - REACT_APP_DD_ENV            (optional, defaults to NODE_ENV)
 *  - REACT_APP_DD_VERSION        (optional)
 *
 * When the required variables are absent (e.g. local development) RUM is not
 * initialized, so the app keeps working without sending data to Datadog.
 */
export const initMonitoring = (): void => {
  if (!applicationId || !clientToken) {
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: process.env.REACT_APP_DD_SITE || 'datadoghq.com',
    service: 'admin-react',
    env: process.env.REACT_APP_DD_ENV || process.env.NODE_ENV,
    version: process.env.REACT_APP_DD_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
};

/**
 * Records a Datadog RUM duration vital while the wrapped component is mounted.
 *
 * Drop-in replacement for the previous Elastic APM `@elastic/apm-rum-react`
 * `withTransaction` HOC, keeping the same call signature so existing usages
 * only need to swap the import path.
 *
 * Usage:
 *  - `export default withTransaction('FactoryDashboard', 'component')(FactoryDashboard);`
 */
export const withTransaction = (name: string, eventType: string) => {
  return function withTransactionHoc<T extends object>(
    Component: ComponentType<T>,
  ): ComponentType<T> {
    const WithTransaction = (props: T) => {
      useEffect(() => {
        const vitalKey = `${name}-${crypto.randomUUID()}`;
        datadogRum.startDurationVital(name, {
          vitalKey,
          context: {eventType},
        });
        return () => {
          datadogRum.stopDurationVital(name, {vitalKey});
        };
      }, []);

      return <Component {...props} />;
    };

    WithTransaction.displayName = `withTransaction(${name})`;

    return WithTransaction;
  };
};
